import React from "react"
import styled, { keyframes } from "styled-components/macro"
import Helmet from "react-helmet"
import { Button as MuiButton, Typography } from "@material-ui/core"
import { useHistory } from "react-router-dom"
import { useSelector } from "react-redux"
import { toAppPath } from "../../utils/allowedRoute"

const REDIRECT_SECONDS = 15

const riseIn = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to { opacity: 1; transform: translateY(0); }
`

const floaty = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-8px); }
`

const drift = keyframes`
  0% { transform: translateY(12px) rotate(0deg); opacity: 0; }
  15% { opacity: 0.7; }
  100% { transform: translateY(-120px) rotate(18deg); opacity: 0; }
`

const Stage = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  min-height: 420px;
  overflow: hidden;

  @media (prefers-reduced-motion: reduce) {
    * {
      animation: none !important;
      transition: none !important;
    }
  }
`

const Particle = styled.span`
  position: absolute;
  top: ${(props) => props.$top};
  left: ${(props) => props.$left};
  width: ${(props) => props.$size}px;
  height: ${(props) => props.$size}px;
  border-radius: ${(props) => props.$radius};
  background: ${(props) => props.$color};
  opacity: 0.45;
  animation: ${drift} ${(props) => props.$duration}s ease-in-out
    ${(props) => props.$delay}s infinite;
  pointer-events: none;
`

const Card = styled.div`
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 560px;
  margin: 12px auto;
  padding: 36px 28px 32px;
  text-align: center;
  background: linear-gradient(180deg, #ffffff 0%, #f7f9fd 100%);
  border: 1px solid #e3eaf5;
  border-radius: 16px;
  box-shadow: 0 12px 40px rgba(32, 82, 194, 0.08);
  animation: ${riseIn} 0.45s ease;
`

const Illustration = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 8px;
  animation: ${floaty} 3.6s ease-in-out infinite;
`

const CountdownWrap = styled.div`
  position: relative;
  width: 108px;
  height: 108px;
  margin: 22px auto 8px;
`

const CountdownValue = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  font-weight: 700;
  color: #e81365;
`

const Ring = styled.svg`
  width: 108px;
  height: 108px;
  transform: rotate(-90deg);

  .ring-progress {
    transition: stroke-dashoffset 1s linear;
  }
`

const Button = styled(MuiButton)`
  && {
    margin-top: 18px;
    min-width: 220px;
    padding: 10px 22px;
    border-radius: 8px;
    text-transform: none;
    font-weight: 600;
    box-shadow: none;
    transition: transform 0.2s ease, box-shadow 0.2s ease;
  }

  &&:hover {
    transform: translateY(-1px);
    box-shadow: 0 8px 18px rgba(55, 111, 208, 0.28);
  }
`

const PARTICLES = [
  { top: "18%", left: "12%", size: 8, color: "#e03b7a", radius: "50%", delay: 0, duration: 7 },
  { top: "70%", left: "18%", size: 6, color: "#e81365", radius: "2px", delay: 1.2, duration: 8 },
  { top: "30%", left: "78%", size: 10, color: "#f13a80", radius: "50%", delay: 0.4, duration: 9 },
  { top: "76%", left: "74%", size: 7, color: "#e81365", radius: "2px", delay: 2, duration: 7.5 },
  { top: "12%", left: "62%", size: 5, color: "#f8347f", radius: "50%", delay: 1.6, duration: 6.5 },
  { top: "58%", left: "88%", size: 9, color: "#e81365", radius: "2px", delay: 0.8, duration: 8.5 },
]

const LockIllustration = () => (
  <svg width="120" height="120" viewBox="0 0 120 120" aria-hidden="true">
    <circle cx="60" cy="60" r="52" fill="#fbe5ee" />
    <path
      d="M60 18l28 10v24c0 20-12 34-28 40-16-6-28-20-28-40V28l28-10z"
      fill="#ffffff"
      stroke="#e81365"
      strokeWidth="3"
    />
    <rect x="46" y="52" width="28" height="22" rx="4" fill="#e81365" />
    <path
      d="M51 52v-6a9 9 0 0 1 18 0v6"
      fill="none"
      stroke="#e81365"
      strokeWidth="3"
      strokeLinecap="round"
    />
    <circle cx="60" cy="62" r="2.5" fill="#ffffff" />
  </svg>
)

function AccessDenied() {
  const history = useHistory()
  const userProfileDetails =
    useSelector((state) => state.adminReducer.userProfileDetails) || {}
  const [secondsLeft, setSecondsLeft] = React.useState(REDIRECT_SECONDS)
  const redirected = React.useRef(false)

  const dashboardPath = React.useMemo(
    () =>
      toAppPath("/dashboard", {
        roleType: userProfileDetails?.roleType || "",
        sourceRole:
          (typeof localStorage !== "undefined" &&
            localStorage.getItem("sourceRole")) ||
          "",
        tenantId:
          (typeof localStorage !== "undefined" &&
            localStorage.getItem("tenantId")) ||
          "",
      }) || "/",
    [userProfileDetails]
  )

  const goToDashboard = React.useCallback(() => {
    if (redirected.current) return
    redirected.current = true
    history.push(dashboardPath)
  }, [dashboardPath, history])

  React.useEffect(() => {
    if (secondsLeft <= 0) {
      goToDashboard()
      return undefined
    }
    const timer = setTimeout(() => {
      setSecondsLeft((value) => value - 1)
    }, 1000)
    return () => clearTimeout(timer)
  }, [goToDashboard, secondsLeft])

  const radius = 46
  const circumference = 2 * Math.PI * radius
  const progress = Math.max(secondsLeft, 0) / REDIRECT_SECONDS
  const dashOffset = circumference * (1 - progress)
  const secondLabel = secondsLeft === 1 ? "second" : "seconds"

  return (
    <Stage>
      <Helmet title="Access Denied" />
      {PARTICLES.map((particle) => (
        <Particle
          key={`${particle.left}-${particle.top}`}
          $top={particle.top}
          $left={particle.left}
          $size={particle.size}
          $color={particle.color}
          $radius={particle.radius}
          $delay={particle.delay}
          $duration={particle.duration}
        />
      ))}
      <Card role="alert">
        <Illustration>
          <LockIllustration />
        </Illustration>
        <Typography component="h2" variant="h5" align="center" gutterBottom>
          Access Denied
        </Typography>
        <Typography component="p" variant="body1" align="center" color="textSecondary">
          You don't have permission to access this page. please contact your administrator if you 
         need access. 
        </Typography>
        <CountdownWrap>
          <Ring viewBox="0 0 108 108" aria-hidden="true">
            <circle cx="54" cy="54" r={radius} fill="none" stroke="#fce6ef" strokeWidth="8" />
            <circle
              className="ring-progress"
              cx="54"
              cy="54"
              r={radius}
              fill="none"
              stroke="#e81365"
              strokeWidth="8"
              strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={dashOffset}
            />
          </Ring>
          <CountdownValue>{Math.max(secondsLeft, 0)}</CountdownValue>
        </CountdownWrap>
        <Typography component="p" variant="body2" align="center" aria-live="polite">
          {`Redirecting to Dashboard in ${Math.max(secondsLeft, 0)} ${secondLabel}`}
        </Typography>
        <Button variant="contained" style={{backgroundColor:'#e81365',color:'#ffff'}} onClick={goToDashboard}>
          Back to Dashboard
        </Button>
      </Card>
    </Stage>
  )
}

export default AccessDenied
